import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { NumericProps } from '@swyftx/aviary/atoms/Typography/Numeric/Numeric.styles';
import { EnhancedTooltip } from '@swyftx/aviary/molecules/EnhancedTooltip/EnhancedTooltip';
import { MarginRisk } from '@swyftx/margin/build/types';

import { formatCurrency } from '@shared/utils';

import { useSwyftxMargin } from 'src/lib/margin';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { getMarginRiskColor, getMarginRiskTitle } from 'src/lib/margin/utils';

import { MarginAccountHealthBar } from '../MarginAccountHealthBar';

type Props = {
  content?: 'numeric' | 'bar';
  size?: NumericProps['size'];
  weight?: NumericProps['weight'];
  className?: string;
};

const MarginAccountHealthMarginLevel: React.FC<Props> = ({ content = 'numeric', size, weight = 'bold', className }) => {
  const { t } = useTranslation('margin');
  const { usedMargin, marginLevel, isLoading } = useMarginAccount();
  const { isUnderMaintenance } = useSwyftxMargin();

  const hasValue = useMemo(() => usedMargin && marginLevel, [marginLevel, usedMargin]);

  return (
    <FlexLayout alignItems='start' justifyContent='space-between' className={className}>
      <FlexLayout spacing={4} direction='column' alignItems='start'>
        <EnhancedTooltip
          name={t('accountOverview.marginLevel.title')}
          title={t('accountOverview.marginLevel.tooltip')}
          content={
            <Numeric
              size='small'
              className='mt-8 bg-color-background-surface-secondary p-12 font-mono text-color-text-primary md:mt-0 md:bg-transparent md:bg-none md:p-0 md:!font-sans md:!text-12 md:text-color-text-inverse'
            >
              {t('accountOverview.marginLevel.content')}
            </Numeric>
          }
          align='start'
          side='top'
        >
          <span>
            <Body size='xsmall' color='secondary' underline='dashed'>
              {t('accountOverview.marginLevel.title')}
            </Body>
          </span>
        </EnhancedTooltip>
        {isUnderMaintenance && !hasValue ? (
          <Numeric weight='bold' size={size} color='primary'>
            {t('maintenance.labels.unavailable')}
          </Numeric>
        ) : (
          <>
            {content === 'numeric' ? (
              <Numeric
                loading={isLoading}
                weight={weight}
                size={size}
                color={getMarginRiskColor(usedMargin, marginLevel)}
              >
                {getMarginRiskTitle(usedMargin, marginLevel) === MarginRisk.None ? '-' : formatCurrency(marginLevel)}%
              </Numeric>
            ) : (
              <MarginAccountHealthBar variant='compact' fontSize={size} showMarginLevel />
            )}
          </>
        )}
      </FlexLayout>
    </FlexLayout>
  );
};

export { MarginAccountHealthMarginLevel };
